import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaUsers, FaPlus, FaTrash, FaRobot, FaLightbulb, FaClipboardList, FaCheckCircle, FaSpinner, FaUserFriends, FaFlask, FaBriefcase, FaHandsHelping, FaCogs, FaPencilAlt, FaVideo, FaBookOpen, FaFileAlt, FaSeedling, FaSearch, FaLinkedin, FaGraduationCap, FaStar } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';
import { supabase } from '../lib/supabase';
import { parseEducation, getAlmamater } from '../utils/education';

const PKM_TYPES = [
  { code: 'PKM-RE', name: 'Riset Eksakta', icon: <FaFlask />, color: 'from-blue-500 to-cyan-500', desc: 'Penelitian di bidang eksakta (sains, teknologi, teknik, matematika)' },
  { code: 'PKM-RSH', name: 'Riset Sosial Humaniora', icon: <FaBookOpen />, color: 'from-emerald-500 to-teal-500', desc: 'Penelitian di bidang sosial, humaniora, pendidikan, ekonomi' },
  { code: 'PKM-K', name: 'Kewirausahaan', icon: <FaBriefcase />, color: 'from-amber-500 to-orange-500', desc: 'Pengembangan usaha kreatif dan inovatif berbasis IPTEK' },
  { code: 'PKM-PM', name: 'Pengabdian Masyarakat', icon: <FaHandsHelping />, color: 'from-pink-500 to-rose-500', desc: 'Penerapan IPTEK untuk membantu masyarakat' },
  { code: 'PKM-PI', name: 'Penerapan Iptek', icon: <FaCogs />, color: 'from-violet-500 to-purple-500', desc: 'Penerapan teknologi tepat guna untuk menyelesaikan masalah' },
  { code: 'PKM-KC', name: 'Karsa Cipta', icon: <FaSeedling />, color: 'from-lime-500 to-green-500', desc: 'Penciptaan karya inovatif berbasis teknologi' },
  { code: 'PKM-GFT', name: 'Gagasan Futuristik Tertulis', icon: <FaPencilAlt />, color: 'from-indigo-500 to-blue-500', desc: 'Gagasan tertulis yang visioner dan solutif' },
  { code: 'PKM-VGK', name: 'Video Gagasan Konstruktif', icon: <FaVideo />, color: 'from-red-500 to-pink-500', desc: 'Gagasan konstruktif dalam format video kreatif' },
  { code: 'PKM-AI', name: 'Artikel Ilmiah', icon: <FaFileAlt />, color: 'from-gray-500 to-slate-600', desc: 'Artikel ilmiah berbasis hasil kajian atau penelitian' },
];

const EMPTY_MEMBER = { name: '', jurusan: '', kampus: '' };

const PkmBuilder = () => {
  usePageTitle('Build Your PKM Proposal');
  const { t } = useLanguage();

  // Steps
  const [step, setStep] = useState(1); // 1=team, 2=AI recommendation, 3=choose PKM type, 4=alumni matching

  // Team data
  const [members, setMembers] = useState([{ ...EMPTY_MEMBER }, { ...EMPTY_MEMBER }, { ...EMPTY_MEMBER }]);
  const [theme, setTheme] = useState('');
  const [title, setTitle] = useState('');

  // AI
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState('');
  const [aiError, setAiError] = useState('');

  // PKM type
  const [selectedPkm, setSelectedPkm] = useState(null);
  const [done, setDone] = useState(false);

  // Alumni matching
  const [matchPhase, setMatchPhase] = useState('idle'); // idle, scanning, analyzing, done
  const [matchedAlumni, setMatchedAlumni] = useState([]);
  const [matchError, setMatchError] = useState('');
  const [scanProgress, setScanProgress] = useState(0);
  const [scannedNames, setScannedNames] = useState([]);
  const [revealedCards, setRevealedCards] = useState([]);

  const addMember = () => {
    if (members.length >= 5) return;
    setMembers([...members, { ...EMPTY_MEMBER }]);
  };

  const removeMember = (idx) => {
    if (members.length <= 2) return;
    setMembers(members.filter((_, i) => i !== idx));
  };

  const updateMember = (idx, field, value) => {
    setMembers(members.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const validMembers = members.filter(m => m.name.trim() && m.jurusan.trim() && m.kampus.trim());

  const canProceedStep1 = validMembers.length >= 2 && theme.trim();

  const getAiRecommendation = async () => {
    setAiLoading(true);
    setAiError('');
    setAiResult('');

    const membersList = validMembers.map((m, i) => `${i + 1}. ${m.name} — Jurusan: ${m.jurusan}, Kampus: ${m.kampus}`).join('\n');

    const prompt = `Kamu adalah konsultan PKM (Program Kreativitas Mahasiswa) yang berpengalaman. Berikut adalah tim yang sedang menyusun proposal PKM:

Anggota Tim:
${membersList}

Tema/Topik yang diminati: ${theme.trim()}
${title.trim() ? `Judul rencana: ${title.trim()}` : ''}

Berdasarkan komposisi jurusan tim di atas, berikan rekomendasi:

1. **Analisis Komposisi Tim**: Apakah komposisi jurusan sudah baik? Apa kelebihan dan kekurangannya?
2. **Rekomendasi Jurusan Tambahan**: Jika tim belum lengkap (kurang dari 5 orang), jurusan apa yang sebaiknya direkrut untuk melengkapi tim?
3. **Pembagian Peran**: Saran pembagian peran berdasarkan kompetensi masing-masing jurusan.
4. **Jenis PKM yang Cocok**: Berdasarkan tema dan komposisi tim, jenis PKM apa yang paling cocok? (PKM-RE, PKM-RSH, PKM-K, PKM-PM, PKM-PI, PKM-KC, PKM-GFT, PKM-VGK, PKM-AI)
5. **Tips Singkat**: 2-3 tips untuk memperkuat proposal.

Gunakan bahasa Indonesia, formatnya ringkas dan mudah dipahami mahasiswa. Gunakan bullet points. PENTING: Jangan gunakan tanda bintang (*) untuk bold atau italic. Tulis teks biasa saja tanpa formatting markdown.`;

    try {
      const response = await fetch('/api/pkm-ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `API error: ${response.status}`);
      }

      if (!data.result) throw new Error('No response from AI');
      setAiResult(data.result);
    } catch (err) {
      setAiError(err.message || 'Gagal mendapatkan rekomendasi AI');
    } finally {
      setAiLoading(false);
    }
  };

  const handleStep1Next = () => {
    setStep(2);
    getAiRecommendation();
  };

  // ─── ALUMNI MATCHING ───
  const startAlumniMatching = useCallback(async () => {
    setMatchPhase('scanning');
    setMatchError('');
    setMatchedAlumni([]);
    setRevealedCards([]);
    setScanProgress(0);
    setScannedNames([]);

    try {
      // Fetch all alumni
      const { data: allAlumni, error } = await supabase
        .from('alumni')
        .select('id, name, education, job, institution, research_interests, photo_url, linkedin, google_scholar')
        .order('id', { ascending: false });

      if (error) throw new Error('Gagal mengambil data alumni');
      if (!allAlumni || allAlumni.length === 0) {
        setMatchPhase('done');
        return;
      }

      // Scanning animation — cycle through alumni names
      const names = allAlumni.map(a => a.name);
      let progress = 0;
      const scanInterval = setInterval(() => {
        progress += 2;
        setScanProgress(Math.min(progress, 90));
        const randomIdx = Math.floor(Math.random() * names.length);
        setScannedNames(prev => {
          const next = [...prev, names[randomIdx]];
          return next.slice(-5); // keep last 5
        });
      }, 120);

      // Build alumni summary for AI — ultra compact, max 30 alumni
      const limitedAlumni = allAlumni.slice(0, 30);
      const alumniSummary = limitedAlumni.map(a => {
        const edu = parseEducation(a.education);
        const parts = [a.id, a.name];
        if (edu.s1_jurusan) parts.push(edu.s1_jurusan);
        if (a.research_interests) parts.push(a.research_interests.split(',').slice(0, 2).map(r => r.trim()).join(','));
        return parts.join('|');
      }).join('\n');

      setMatchPhase('analyzing');

      const prompt = `Cocokkan alumni untuk tim PKM.
Tim: ${validMembers.map(m => `${m.name}(${m.jurusan})`).join(', ')}
Tema: ${theme.trim()}
PKM: ${selectedPkm?.code}

Alumni:
${alumniSummary}

Pilih max 3 alumni paling cocok. Balas JSON saja:
[{"id":number,"reason":"alasan singkat","relevance":skor 1-100}]
Jika tidak cocok: []`;

      const response = await fetch('/api/pkm-ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });

      const data = await response.json();
      clearInterval(scanInterval);
      setScanProgress(100);

      if (!response.ok) throw new Error(data.error || 'AI matching failed');

      // Parse AI response
      let matches = [];
      try {
        const cleaned = (data.result || '').replace(/```json?\n?/g, '').replace(/```/g, '').trim();
        matches = JSON.parse(cleaned);
      } catch {
        // Try to extract JSON array from response
        const jsonMatch = (data.result || '').match(/\[.*\]/s);
        if (jsonMatch) matches = JSON.parse(jsonMatch[0]);
      }

      // Enrich matched alumni with full data
      const enriched = matches
        .filter(m => m.id && typeof m.id === 'number')
        .slice(0, 3)
        .map(m => {
          const alumni = allAlumni.find(a => a.id === m.id);
          if (!alumni) return null;
          return { ...alumni, reason: m.reason, relevance: m.relevance || 50 };
        })
        .filter(Boolean)
        .sort((a, b) => (b.relevance || 0) - (a.relevance || 0));

      setMatchedAlumni(enriched);
      setMatchPhase('done');

      // Reveal cards one by one with delay
      enriched.forEach((_, i) => {
        setTimeout(() => {
          setRevealedCards(prev => [...prev, i]);
        }, 600 * (i + 1));
      });

    } catch (err) {
      setMatchError(err.message || 'Gagal melakukan matching');
      setMatchPhase('done');
    }
  }, [theme, selectedPkm, validMembers]);

  const handleGoToStep4 = () => {
    setStep(4);
    startAlumniMatching();
  };

  // Cleanup effect not needed since we handle intervals inside the async function

  // Format AI markdown to simple HTML
  const formatAiText = (text) => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^\s*[-•]\s+(.+)/gm, '<li>$1</li>')
      .replace(/^\s*\d+\.\s+(.+)/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>\n?)+/g, '<ul class="list-disc pl-5 space-y-1 my-2">$&</ul>')
      .replace(/\n{2,}/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center bg-white dark:bg-dark-800 rounded-2xl shadow-xl p-8 border border-gray-100 dark:border-dark-700">
          <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="text-4xl text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{t('pkmBuilder.doneTitle')}</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-2">{t('pkmBuilder.doneDesc')}</p>

          <div className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{t('pkmBuilder.summary')}</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">{t('pkmBuilder.pkmType')}</span>
                <span className="font-bold text-primary-600 dark:text-primary-400">{selectedPkm?.code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">{t('pkmBuilder.teamSize')}</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{validMembers.length} orang</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">{t('pkmBuilder.themeLabel')}</span>
                <p className="font-medium text-gray-800 dark:text-gray-200 mt-0.5">{theme}</p>
              </div>
              {title && (
                <div>
                  <span className="text-gray-500 dark:text-gray-400">{t('pkmBuilder.titleLabel')}</span>
                  <p className="font-medium text-gray-800 dark:text-gray-200 mt-0.5">{title}</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Link to="/pkm-corner" className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors text-sm">
              {t('pkmBuilder.backToCorner')}
            </Link>
            <Link to="/pkm-bank" className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 px-6 rounded-xl transition-colors text-sm">
              {t('pkmBuilder.openBank')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8">
          <Link to="/pkm-corner" className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 text-sm font-medium mb-4 transition-colors">
            <FaArrowLeft className="text-xs" /> {t('pkmBuilder.backToCorner')}
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <FaRobot className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white">{t('pkmBuilder.title')}</h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm">{t('pkmBuilder.subtitle')}</p>
            </div>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-8">
          {[
            { num: 1, label: t('pkmBuilder.stepTeam') },
            { num: 2, label: t('pkmBuilder.stepAI') },
            { num: 3, label: t('pkmBuilder.stepPKM') },
            { num: 4, label: t('pkmBuilder.stepMatch') },
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              <div className={`flex items-center gap-2 ${step >= s.num ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-600'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  step > s.num ? 'bg-green-500 text-white' :
                  step === s.num ? 'bg-primary-600 text-white' :
                  'bg-gray-200 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                }`}>
                  {step > s.num ? '✓' : s.num}
                </div>
                <span className="text-xs font-medium hidden sm:inline">{s.label}</span>
              </div>
              {i < 3 && <div className={`flex-1 h-0.5 rounded ${step > s.num ? 'bg-green-400' : 'bg-gray-200 dark:bg-dark-700'}`}></div>}
            </React.Fragment>
          ))}
        </div>

        {/* ─── STEP 1: BUILD TEAM ─── */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Team members */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6 sm:p-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <FaUsers className="text-primary-500" /> {t('pkmBuilder.teamTitle')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t('pkmBuilder.teamDesc')}</p>

              <div className="space-y-4">
                {members.map((m, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-dark-700 rounded-xl p-4 border border-gray-100 dark:border-dark-600">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {idx === 0 ? `👑 ${t('pkmBuilder.leader')}` : `${t('pkmBuilder.member')} ${idx + 1}`}
                      </span>
                      {members.length > 2 && (
                        <button onClick={() => removeMember(idx)} className="text-red-400 hover:text-red-600 text-sm transition-colors p-1">
                          <FaTrash />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={m.name}
                        onChange={(e) => updateMember(idx, 'name', e.target.value)}
                        placeholder={t('pkmBuilder.namePh')}
                        className="w-full border border-gray-300 dark:border-dark-600 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-800 dark:text-white transition-colors"
                      />
                      <input
                        type="text"
                        value={m.jurusan}
                        onChange={(e) => updateMember(idx, 'jurusan', e.target.value)}
                        placeholder={t('pkmBuilder.majorPh')}
                        className="w-full border border-gray-300 dark:border-dark-600 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-800 dark:text-white transition-colors"
                      />
                      <input
                        type="text"
                        value={m.kampus}
                        onChange={(e) => updateMember(idx, 'kampus', e.target.value)}
                        placeholder={t('pkmBuilder.campusPh')}
                        className="w-full border border-gray-300 dark:border-dark-600 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-800 dark:text-white transition-colors"
                      />
                    </div>
                  </div>
                ))}
              </div>

              {members.length < 5 && (
                <button
                  onClick={addMember}
                  className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 px-4 py-2.5 rounded-xl transition-colors"
                >
                  <FaPlus className="text-xs" /> {t('pkmBuilder.addMember')} ({members.length}/5)
                </button>
              )}
            </div>

            {/* Theme & Title */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6 sm:p-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <FaLightbulb className="text-amber-500" /> {t('pkmBuilder.ideaTitle')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t('pkmBuilder.ideaDesc')}</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    {t('pkmBuilder.themeLabel')} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                    placeholder={t('pkmBuilder.themePh')}
                    className="w-full border border-gray-300 dark:border-dark-600 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:text-white transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    {t('pkmBuilder.titleLabel')} <span className="text-gray-400 text-xs font-normal">({t('common.optional')})</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('pkmBuilder.titlePh')}
                    className="w-full border border-gray-300 dark:border-dark-600 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:text-white transition-colors"
                  />
                </div>
              </div>
            </div>

            {/* Next button */}
            <button
              onClick={handleStep1Next}
              disabled={!canProceedStep1}
              className="w-full bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {t('pkmBuilder.getRecommendation')} <FaArrowRight className="text-sm" />
            </button>
          </div>
        )}

        {/* ─── STEP 2: AI RECOMMENDATION ─── */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Team summary */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 p-5">
              <div className="flex items-center gap-3 mb-3">
                <FaUserFriends className="text-primary-500" />
                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">{t('pkmBuilder.yourTeam')}</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {validMembers.map((m, i) => (
                  <div key={i} className="bg-gray-50 dark:bg-dark-700 rounded-lg px-3 py-2 text-xs border border-gray-100 dark:border-dark-600">
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{m.name}</span>
                    <span className="text-gray-400 dark:text-gray-500 mx-1">·</span>
                    <span className="text-gray-500 dark:text-gray-400">{m.jurusan}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                {t('pkmBuilder.themeLabel')}: <span className="text-gray-600 dark:text-gray-300 font-medium">{theme}</span>
                {title && <> · {t('pkmBuilder.titleLabel')}: <span className="text-gray-600 dark:text-gray-300 font-medium">{title}</span></>}
              </p>
            </div>

            {/* AI Result */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6 sm:p-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FaRobot className="text-violet-500" /> {t('pkmBuilder.aiTitle')}
              </h2>

              {aiLoading && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                  <FaSpinner className="text-3xl animate-spin text-violet-500 mb-4" />
                  <p className="text-sm font-medium">{t('pkmBuilder.aiLoading')}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('pkmBuilder.aiLoadingDesc')}</p>
                </div>
              )}

              {aiError && (
                <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 rounded-2xl p-6 mb-4 text-center">
                  <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-800/30 flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">☕</span>
                  </div>
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">Fitur sedang sibuk</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400/80 mb-4">Banyak yang sedang menggunakan fitur ini. Silakan coba lagi dalam beberapa saat.</p>
                  <button
                    onClick={getAiRecommendation}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium transition-colors shadow-sm"
                  >
                    <FaRobot className="text-xs" /> Coba Lagi
                  </button>
                </div>
              )}

              {aiResult && (
                <div
                  className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed ai-result"
                  dangerouslySetInnerHTML={{ __html: formatAiText(aiResult) }}
                />
              )}
            </div>

            {/* Navigation */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300 font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <FaArrowLeft className="text-sm" /> {t('pkmBuilder.editTeam')}
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={aiLoading}
                className="flex-1 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-300 disabled:opacity-40 flex items-center justify-center gap-2"
              >
                {t('pkmBuilder.choosePKM')} <FaArrowRight className="text-sm" />
              </button>
            </div>
          </div>
        )}

        {/* ─── STEP 3: CHOOSE PKM TYPE ─── */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6 sm:p-8">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <FaClipboardList className="text-emerald-500" /> {t('pkmBuilder.choosePKMTitle')}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t('pkmBuilder.choosePKMDesc')}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {PKM_TYPES.map(pkm => (
                  <button
                    key={pkm.code}
                    onClick={() => setSelectedPkm(pkm)}
                    className={`relative text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                      selectedPkm?.code === pkm.code
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-md ring-2 ring-primary-200 dark:ring-primary-800'
                        : 'border-gray-100 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 hover:border-gray-300 dark:hover:border-dark-500 hover:shadow-sm'
                    }`}
                  >
                    {selectedPkm?.code === pkm.code && (
                      <div className="absolute top-2 right-2">
                        <FaCheckCircle className="text-primary-500 text-sm" />
                      </div>
                    )}
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${pkm.color} flex items-center justify-center text-white mb-2.5 text-sm`}>
                      {pkm.icon}
                    </div>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">{pkm.code}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{pkm.name}</p>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 leading-relaxed">{pkm.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                className="flex-1 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300 font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <FaArrowLeft className="text-sm" /> {t('pkmBuilder.backToAI')}
              </button>
              <button
                onClick={handleGoToStep4}
                disabled={!selectedPkm}
                className="flex-1 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {t('pkmBuilder.findMentor')} <FaArrowRight className="text-sm" />
              </button>
            </div>
          </div>
        )}

        {/* ─── STEP 4: ALUMNI MATCHING (Beta) ─── */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-lg border border-gray-100 dark:border-dark-700 p-6 sm:p-8">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <FaSearch className="text-violet-500" /> {t('pkmBuilder.matchTitle')}
                </h2>
                <span className="text-[10px] font-bold bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 px-2 py-0.5 rounded-full uppercase tracking-wider">Beta</span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t('pkmBuilder.matchDesc')}</p>

              {/* Scanning animation */}
              {(matchPhase === 'scanning' || matchPhase === 'analyzing') && (
                <div className="flex flex-col items-center py-8">
                  {/* Radar / scan visual */}
                  <div className="relative w-40 h-40 mb-6">
                    {/* Outer ring */}
                    <div className="absolute inset-0 rounded-full border-2 border-violet-200 dark:border-violet-800 opacity-40"></div>
                    {/* Middle ring */}
                    <div className="absolute inset-4 rounded-full border-2 border-violet-300 dark:border-violet-700 opacity-50"></div>
                    {/* Inner ring */}
                    <div className="absolute inset-8 rounded-full border-2 border-violet-400 dark:border-violet-600 opacity-60"></div>
                    {/* Center dot */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-4 h-4 rounded-full bg-violet-500 animate-ping"></div>
                      <div className="absolute w-4 h-4 rounded-full bg-violet-600"></div>
                    </div>
                    {/* Scanning sweep */}
                    <div className="absolute inset-0 rounded-full overflow-hidden">
                      <div className="absolute top-1/2 left-1/2 w-1/2 h-[2px] bg-gradient-to-r from-violet-500 to-transparent origin-left animate-spin" style={{ animationDuration: '2s' }}></div>
                    </div>
                    {/* Floating dots (representing alumni being scanned) */}
                    {[0, 1, 2, 3, 4].map(i => (
                      <div
                        key={i}
                        className="absolute w-2 h-2 rounded-full bg-violet-400 dark:bg-violet-300 animate-pulse"
                        style={{
                          top: `${20 + Math.sin(i * 1.2) * 30 + 10}%`,
                          left: `${20 + Math.cos(i * 1.5) * 30 + 10}%`,
                          animationDelay: `${i * 0.3}s`,
                          opacity: 0.4 + Math.random() * 0.6,
                        }}
                      ></div>
                    ))}
                  </div>

                  {/* Progress bar */}
                  <div className="w-full max-w-xs mb-4">
                    <div className="h-2 bg-gray-200 dark:bg-dark-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${scanProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-1.5">{scanProgress}%</p>
                  </div>

                  {/* Phase text */}
                  <p className="text-sm font-semibold text-violet-600 dark:text-violet-400 mb-2">
                    {matchPhase === 'scanning' ? t('pkmBuilder.matchScanning') : t('pkmBuilder.matchAnalyzing')}
                  </p>

                  {/* Scrolling names */}
                  <div className="h-8 overflow-hidden relative w-full max-w-xs">
                    <div className="flex flex-col items-center gap-1 animate-pulse">
                      {scannedNames.slice(-2).map((name, i) => (
                        <p key={`${name}-${i}`} className={`text-xs font-mono truncate ${i === scannedNames.slice(-2).length - 1 ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>
                          {name}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Error — friendly busy message */}
              {matchError && matchPhase === 'done' && (
                <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-700/30 rounded-2xl p-6 mb-4 text-center">
                  <div className="w-14 h-14 rounded-full bg-amber-100 dark:bg-amber-800/30 flex items-center justify-center mx-auto mb-3">
                    <span className="text-2xl">☕</span>
                  </div>
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">Fitur sedang sibuk</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400/80 mb-4">Banyak yang sedang menggunakan fitur ini. Silakan coba lagi dalam beberapa saat.</p>
                  <button
                    onClick={startAlumniMatching}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium transition-colors shadow-sm"
                  >
                    <FaSearch className="text-xs" /> Coba Lagi
                  </button>
                </div>
              )}

              {/* Results */}
              {matchPhase === 'done' && !matchError && (
                <div>
                  {matchedAlumni.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-dark-700 flex items-center justify-center mx-auto mb-4">
                        <FaUsers className="text-2xl text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('pkmBuilder.matchNoResult')}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <FaStar className="text-amber-500" />
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                          {t('pkmBuilder.matchFound').replace('{count}', matchedAlumni.length)}
                        </p>
                      </div>

                      {matchedAlumni.map((alumni, idx) => {
                        const edu = parseEducation(alumni.education);
                        const isRevealed = revealedCards.includes(idx);
                        return (
                          <div
                            key={alumni.id}
                            className={`relative bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 border border-violet-200 dark:border-violet-700/40 rounded-2xl p-5 transition-all duration-700 ${
                              isRevealed ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
                            }`}
                          >
                            {/* Relevance badge */}
                            <div className="absolute top-3 right-3">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                alumni.relevance >= 80
                                  ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                                  : alumni.relevance >= 50
                                  ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                              }`}>
                                {alumni.relevance}% match
                              </span>
                            </div>

                            <div className="flex gap-4">
                              {/* Photo */}
                              <div className="shrink-0">
                                {alumni.photo_url ? (
                                  <img
                                    src={alumni.photo_url}
                                    alt={alumni.name}
                                    className="w-16 h-16 rounded-xl object-cover border-2 border-white dark:border-dark-600 shadow-md"
                                  />
                                ) : (
                                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-violet-400 to-indigo-500 flex items-center justify-center text-white text-xl font-bold shadow-md">
                                    {alumni.name?.charAt(0)}
                                  </div>
                                )}
                              </div>

                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <h4 className="text-base font-bold text-gray-900 dark:text-white truncate">{alumni.name}</h4>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <FaGraduationCap className="text-xs text-violet-500 shrink-0" />
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {edu.s1_jurusan && `${edu.s1_jurusan}`} {edu.s1_kampus && `· ${getAlmamater(edu.s1_kampus)}`}
                                  </p>
                                </div>
                                {alumni.job && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                    {alumni.job}{alumni.institution ? ` di ${alumni.institution}` : ''}
                                  </p>
                                )}
                                {alumni.research_interests && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {alumni.research_interests.split(',').slice(0, 3).map((r, i) => (
                                      <span key={i} className="text-[10px] bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300 px-2 py-0.5 rounded-full">
                                        {r.trim()}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* AI reason */}
                            {alumni.reason && (
                              <div className="mt-3 bg-white/60 dark:bg-dark-800/60 rounded-lg px-3 py-2 border border-violet-100 dark:border-violet-800/40">
                                <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                                  <FaRobot className="inline text-violet-500 mr-1" />
                                  {alumni.reason}
                                </p>
                              </div>
                            )}

                            {/* Links */}
                            <div className="flex gap-2 mt-3">
                              <Link
                                to={`/alumni/${alumni.id}`}
                                className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 bg-primary-50 dark:bg-primary-900/20 px-3 py-1.5 rounded-lg transition-colors"
                              >
                                {t('pkmBuilder.viewProfile')}
                              </Link>
                              {alumni.linkedin && (
                                <a
                                  href={alumni.linkedin}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 bg-blue-50 dark:bg-blue-900/20 px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                                >
                                  <FaLinkedin /> LinkedIn
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="flex gap-3">
              <button
                onClick={() => setStep(3)}
                className="flex-1 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 text-gray-700 dark:text-gray-300 font-semibold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <FaArrowLeft className="text-sm" /> {t('pkmBuilder.backToPKM')}
              </button>
              <button
                onClick={() => setDone(true)}
                disabled={matchPhase !== 'done'}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <FaCheckCircle className="text-sm" /> {t('pkmBuilder.finish')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PkmBuilder;
